import React, { useState, useEffect, useCallback } from 'react'
import {
  useDataProvider,
  useNotify,
  useRecordContext,
  usePermissions,
} from 'react-admin'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Button,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tooltip,
  InputAdornment,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  PowerSettingsNew as ToggleIcon,
  Key as KeyIcon,
} from '@mui/icons-material'
import userPermissions from '../Users/userPermissions'

interface ApiKey {
  id: number
  name: string
  key_prefix: string
  created_at: string
  last_used_at: string | null
  expires_at: string | null
  is_active: boolean
  permissions: number
  permissions_list: string[]
}

const AVAILABLE_PERMISSIONS = [
  { value: 'LOGIN', label: 'Login (Basic Access)' },
  { value: 'VIEW_USERS', label: 'View Users' },
  { value: 'CREATE_USERS', label: 'Create Users' },
  { value: 'EDIT_USERS', label: 'Edit Users' },
  { value: 'DELETE_USERS', label: 'Delete Users' },
  { value: 'VIEW_TRANSACTIONS', label: 'View Transactions' },
  { value: 'CREATE_TRANSACTIONS', label: 'Create Transactions' },
  { value: 'EDIT_TRANSACTIONS', label: 'Edit Transactions' },
  { value: 'DELETE_TRANSACTIONS', label: 'Delete Transactions' },
  { value: 'VIEW_TAG_CATEGORIES', label: 'View Tag Categories' },
  { value: 'CREATE_TAG_CATEGORIES', label: 'Create Tag Categories' },
  { value: 'EDIT_TAG_CATEGORIES', label: 'Edit Tag Categories' },
  { value: 'SUPER_ADMIN', label: 'Super Admin (Full Access)' },
]

const OrganisationApiKeys = () => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const record = useRecordContext()
  const { permissions } = usePermissions()

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [keyToDelete, setKeyToDelete] = useState<number | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [newKeyResponse, setNewKeyResponse] = useState<any>(null)
  const [showApiKey, setShowApiKey] = useState(false)

  // Form state
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyDescription, setNewKeyDescription] = useState('')
  const [expiresInDays, setExpiresInDays] = useState<number | null>(365)
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])

  // Check if user has SUPER_ADMIN permission
  const isSuperAdmin = permissions && permissions & userPermissions.SUPER_ADMIN

  // Load API keys
  const loadApiKeys = useCallback(async () => {
    if (!isSuperAdmin) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await dataProvider.getApiKeys()
      setApiKeys(response || [])
    } catch (error) {
      console.error('Error loading API keys:', error)
      notify('Error loading API keys', { type: 'error' })
      setApiKeys([])
    } finally {
      setLoading(false)
    }
  }, [dataProvider, isSuperAdmin, notify])

  useEffect(() => {
    loadApiKeys()
  }, [loadApiKeys])

  const handleOpenCreateDialog = () => {
    setNewKeyName('')
    setNewKeyDescription('')
    setExpiresInDays(365)
    setSelectedPermissions([])
    setNewKeyResponse(null)
    setIsCreateDialogOpen(true)
  }

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false)
    setNewKeyResponse(null)
    setShowApiKey(false)
  }

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      notify('API key name is required', { type: 'warning' })
      return
    }

    if (selectedPermissions.length === 0) {
      notify('Please select at least one permission', { type: 'warning' })
      return
    }

    try {
      setIsCreating(true)
      const response = await dataProvider.createApiKey(
        newKeyName,
        newKeyDescription,
        expiresInDays,
        selectedPermissions
      )
      setNewKeyResponse(response)
      notify('API key created successfully', { type: 'success' })
      loadApiKeys()
    } catch (error: any) {
      console.error('Error creating API key:', error)
      notify(error.message || 'Error creating API key', { type: 'error' })
    } finally {
      setIsCreating(false)
    }
  }

  const handleCopyApiKey = () => {
    if (newKeyResponse?.api_key) {
      navigator.clipboard.writeText(newKeyResponse.api_key)
      notify('API key copied to clipboard', { type: 'success' })
    }
  }

  const handleToggleKey = async (keyId: number) => {
    try {
      await dataProvider.toggleApiKey(keyId)
      notify('API key status updated', { type: 'success' })
      loadApiKeys()
    } catch (error: any) {
      console.error('Error toggling API key:', error)
      notify(error.message || 'Error updating API key', { type: 'error' })
    }
  }

  const handleOpenDeleteDialog = (keyId: number) => {
    setKeyToDelete(keyId)
    setIsDeleteDialogOpen(true)
  }

  const handleCloseDeleteDialog = () => {
    setKeyToDelete(null)
    setIsDeleteDialogOpen(false)
  }

  const handleDeleteKey = async () => {
    if (!keyToDelete) return

    try {
      await dataProvider.revokeApiKey(keyToDelete)
      notify('API key revoked successfully', { type: 'success' })
      loadApiKeys()
      handleCloseDeleteDialog()
    } catch (error: any) {
      console.error('Error revoking API key:', error)
      notify(error.message || 'Error revoking API key', { type: 'error' })
    }
  }

  const handlePermissionToggle = (permission: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((p) => p !== permission)
        : [...prev, permission]
    )
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleString()
  }

  if (!isSuperAdmin) {
    return (
      <Box p={2}>
        <Alert severity="warning">
          You need SUPER_ADMIN permissions to manage API keys.
        </Alert>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box p={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">API Keys</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Create API Key
        </Button>
      </Box>

      <Typography variant="body2" color="textSecondary" paragraph>
        API keys allow automated systems to access the API without user login.
        Keys are scoped to this organisation.
      </Typography>

      {apiKeys.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <KeyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="textSecondary">
            No API keys created yet. Click &quot;Create API Key&quot; to get
            started.
          </Typography>
        </Paper>
      ) : (
        <List>
          {apiKeys.map((key) => (
            <Paper key={key.id} sx={{ mb: 2 }}>
              <ListItem>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle1">{key.name}</Typography>
                      <Chip
                        label={key.is_active ? 'Active' : 'Inactive'}
                        size="small"
                        color={key.is_active ? 'success' : 'default'}
                      />
                      {key.expires_at &&
                        new Date(key.expires_at) < new Date() && (
                          <Chip label="Expired" size="small" color="error" />
                        )}
                    </Box>
                  }
                  secondary={
                    <Box mt={1}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Key:</strong> {key.key_prefix}...
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Created:</strong> {formatDate(key.created_at)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Last Used:</strong>{' '}
                        {formatDate(key.last_used_at)}
                      </Typography>
                      {key.expires_at && (
                        <Typography variant="body2" color="textSecondary">
                          <strong>Expires:</strong> {formatDate(key.expires_at)}
                        </Typography>
                      )}
                      <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                        {key.permissions_list.map((perm) => (
                          <Chip key={perm} label={perm} size="small" />
                        ))}
                      </Box>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title={key.is_active ? 'Disable' : 'Enable'}>
                    <IconButton
                      edge="end"
                      onClick={() => handleToggleKey(key.id)}
                      sx={{ mr: 1 }}
                    >
                      <ToggleIcon
                        color={key.is_active ? 'primary' : 'disabled'}
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Revoke">
                    <IconButton
                      edge="end"
                      onClick={() => handleOpenDeleteDialog(key.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      {/* Create API Key Dialog */}
      <Dialog
        open={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {newKeyResponse ? 'API Key Created' : 'Create New API Key'}
        </DialogTitle>
        <DialogContent>
          {newKeyResponse ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Important:</strong> Save this API key now. You
                won&apos;t be able to see it again!
              </Alert>
              <TextField
                label="API Key"
                fullWidth
                value={newKeyResponse.api_key}
                type={showApiKey ? 'text' : 'password'}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowApiKey(!showApiKey)}>
                        {showApiKey ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                      <IconButton onClick={handleCopyApiKey} color="primary">
                        <CopyIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="textSecondary">
                Key Prefix: {newKeyResponse.key_prefix}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Permissions: {newKeyResponse.permissions_list.join(', ')}
              </Typography>
            </Box>
          ) : (
            <Box>
              <TextField
                autoFocus
                margin="dense"
                label="Name"
                fullWidth
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                helperText="A descriptive name for this API key"
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Description (optional)"
                fullWidth
                multiline
                rows={2}
                value={newKeyDescription}
                onChange={(e) => setNewKeyDescription(e.target.value)}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Expires In</InputLabel>
                <Select
                  value={expiresInDays || ''}
                  onChange={(e) =>
                    setExpiresInDays(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                  label="Expires In"
                >
                  <MenuItem value="">Never</MenuItem>
                  <MenuItem value={30}>30 days</MenuItem>
                  <MenuItem value={90}>90 days</MenuItem>
                  <MenuItem value={180}>180 days</MenuItem>
                  <MenuItem value={365}>1 year</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="subtitle2" gutterBottom>
                Permissions
              </Typography>
              <FormGroup>
                {AVAILABLE_PERMISSIONS.map((perm) => (
                  <FormControlLabel
                    key={perm.value}
                    control={
                      <Checkbox
                        checked={selectedPermissions.includes(perm.value)}
                        onChange={() => handlePermissionToggle(perm.value)}
                      />
                    }
                    label={perm.label}
                  />
                ))}
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {newKeyResponse ? (
            <Button onClick={handleCloseCreateDialog} color="primary">
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseCreateDialog}>Cancel</Button>
              <Button
                onClick={handleCreateKey}
                color="primary"
                variant="contained"
                disabled={isCreating}
              >
                {isCreating ? 'Creating...' : 'Create'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Revoke API Key?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently revoke the API key. Any applications using
            this key will no longer be able to authenticate.
          </Typography>
          <Typography color="error" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteKey} color="error" variant="contained">
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default OrganisationApiKeys
